const BACKSLASH = /\\/;

function containsControlCharacters(value: string): boolean {
    return Array.from(value).some((character) => {
        const codePoint = character.codePointAt(0) ?? 0;
        return codePoint <= 0x1f || codePoint === 0x7f;
    });
}

/**
 * Return a normalized same-origin application path, or `null` when a value
 * could make React Router or the browser leave the current origin.
 *
 * Backslashes are rejected before URL parsing because browsers normalize them
 * to forward slashes for special schemes. Repeated decoding closes the same
 * escape hatch when the separator is percent-encoded one or more times.
 */
export function safeInternalPath(value: unknown): string | null {
    if (typeof value !== 'string' || value.length === 0 || value !== value.trim()) {
        return null;
    }

    let decoded = value;
    let encodingIsStable = false;
    for (let depth = 0; depth < 8; depth += 1) {
        if (
            !decoded.startsWith('/') ||
            decoded.startsWith('//') ||
            BACKSLASH.test(decoded) ||
            containsControlCharacters(decoded)
        ) {
            return null;
        }

        try {
            const next = decodeURIComponent(decoded);
            if (next === decoded) {
                encodingIsStable = true;
                break;
            }
            decoded = next;
        } catch {
            return null;
        }
    }

    if (
        !encodingIsStable ||
        !decoded.startsWith('/') ||
        decoded.startsWith('//') ||
        BACKSLASH.test(decoded) ||
        containsControlCharacters(decoded)
    ) {
        return null;
    }

    try {
        const origin = window.location.origin;
        const resolved = new URL(value, origin);
        if (resolved.origin !== origin) return null;
        return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch {
        return null;
    }
}

/** Safely retain the path, query, and fragment from a React Router location. */
export function safeInternalLocation(value: unknown): string | null {
    if (!value || typeof value !== 'object') return null;

    const candidate = value as { pathname?: unknown; search?: unknown; hash?: unknown };
    if (typeof candidate.pathname !== 'string') return null;
    if (candidate.search != null && typeof candidate.search !== 'string') return null;
    if (candidate.hash != null && typeof candidate.hash !== 'string') return null;

    const search = candidate.search ?? '';
    const hash = candidate.hash ?? '';
    if (search && !search.startsWith('?')) return null;
    if (hash && !hash.startsWith('#')) return null;

    return safeInternalPath(`${candidate.pathname}${search}${hash}`);
}
